import { useEffect, useMemo, useState, type ReactNode } from "react";
import { Navigate } from "react-router-dom";

import { authService } from "@/services/authService";
import { SessionContext, type SessionContextValue } from "@/state/session";
import type { User } from "@/types/user";

/**
 * Loads the current session once on mount (user identity + CSRF token) and makes
 * it available app-wide. When served behind the auth gateway the user is always
 * present; in the unauthenticated dev case `user` stays null and the UI degrades
 * gracefully (the user menu hides itself).
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<SessionContextValue["status"]>("loading");
  const [user, setUser] = useState<User | null>(null);
  const [csrfToken, setCsrfToken] = useState<string | null>(null);

  useEffect(() => {
    let active = true;

    void (async () => {
      const currentUser = await authService.getCurrentUser();
      if (!active) return;
      setUser(currentUser);

      if (currentUser) {
        const token = await authService.getCsrfToken();
        if (active) setCsrfToken(token);
      }
      if (active) setStatus("ready");
    })();

    return () => {
      active = false;
    };
  }, []);

  const value = useMemo<SessionContextValue>(
    () => ({ status, user, csrfToken, permissions: user?.permissions ?? [] }),
    [status, user, csrfToken]
  );

  // Don't render anything until the session check resolves — avoids a flash of
  // the dashboard before we know who (if anyone) is signed in.
  if (status === "loading") return null;

  // No session → send unauthenticated visitors to the public login page rather
  // than rendering the app shell. The /login route lives outside AuthProvider,
  // so this can't loop.
  if (!user) return <Navigate to="/login" replace />;

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}
